import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useMemo } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import {
  ModuleLayout,
  getModuleThemeColors,
} from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { BlockRows } from "@brexhq/next/src/components/shared/BlockRows/BlockRows";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import { ImageBorderRadiusWrapper } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import type { IBynderImageWithBorderRadius } from "@brexhq/shared/types/BynderAsset";
import {
  type ColNumber,
  type ColumnProps,
  type MEDColNumber,
  maxDesktopGridSpan,
  maxTabletGridSpan,
  SMLGridColumnEnd,
} from "@brexhq/shared/types/Grid";
import {
  SpacingValues,
  desktop as spacingDesktop,
  tablet as spacingTablet,
  mobile as spacingMobile,
} from "@brexhq/shared/types/SpacingScale";
import {
  TextOverImageData,
  TextOverImageFill,
  TextOverImageHorizontalAlignment,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextOverImage";

import { CustomBreakpoints } from "../../../contexts/Theme";
import { SpacingArrayValues } from "../../layout/Spacers/types";
import { Grid, Column } from "../../shared/Grid";

import { ScrimMedia } from "./components/ScrimMedia";

export type ResponsiveHeight = {
  mobile: string;
  desktop: string;
};

type ResponsiveHorizontalPaddingConfig = {
  desktop: SpacingValues;
  tablet: SpacingValues;
  mobile: SpacingValues;
};

type HorizontalPaddingInput =
  | {
      desktop?: SpacingValues | null;
      tablet?: SpacingValues | null;
      mobile?: SpacingValues | null;
    }
  | null
  | undefined;

const DEFAULT_HORIZONTAL_PADDING: ResponsiveHorizontalPaddingConfig = {
  desktop: "lrg72",
  tablet: "lrg",
  mobile: "sml",
};

const coerceSpacingValue = (
  value: SpacingValues | null | undefined,
  fallback: SpacingValues,
): SpacingValues => value ?? fallback;

const resolveHorizontalPadding = (
  padding: HorizontalPaddingInput,
): ResponsiveHorizontalPaddingConfig => ({
  desktop: coerceSpacingValue(
    padding?.desktop,
    DEFAULT_HORIZONTAL_PADDING.desktop,
  ),
  tablet: coerceSpacingValue(
    padding?.tablet,
    DEFAULT_HORIZONTAL_PADDING.tablet,
  ),
  mobile: coerceSpacingValue(
    padding?.mobile,
    DEFAULT_HORIZONTAL_PADDING.mobile,
  ),
});

const FullHeightGrid = styled(Grid, {
  shouldForwardProp: (prop) =>
    prop !== "height" && prop !== "minHeight" && prop !== "horizontalPadding",
})<{
  height: ResponsiveHeight;
  minHeight?: number | null;
  horizontalPadding: ResponsiveHorizontalPaddingConfig;
}>(({ theme, height, minHeight, horizontalPadding }) => {
  return {
    ...(minHeight && { minHeight: `${minHeight}px` }),
    "> div": {
      minHeight: height.desktop,
      paddingLeft: `${spacingDesktop[horizontalPadding.desktop]}px`,
      paddingRight: `${spacingDesktop[horizontalPadding.desktop]}px`,
      [theme.breakpoints.MED]: {
        paddingLeft: `${spacingTablet[horizontalPadding.tablet]}px`,
        paddingRight: `${spacingTablet[horizontalPadding.tablet]}px`,
      },
      [theme.breakpoints.SML]: {
        minHeight: height.mobile,
        paddingLeft: `${spacingMobile[horizontalPadding.mobile]}px`,
        paddingRight: `${spacingMobile[horizontalPadding.mobile]}px`,
      },
    },
  };
});

const TopColumn = styled(Column)`
  z-index: 2;
`;

const DesktopMedia = styled(BynderAsset)<{
  height: string;
  position?: React.CSSProperties["position"];
  imageAlignment?: string | null;
}>(({ theme, height, position, imageAlignment }) => {
  return {
    position,
    objectFit: "cover",
    display: "block",
    width: "100%",
    height: height,
    [theme.breakpoints.SML]: {
      display: "none",
    },
    ...(imageAlignment && {
      objectPosition: imageAlignment,
    }),
  };
});

const MobileMedia = styled(BynderAsset)<{
  height: string;
  position?: React.CSSProperties["position"];
}>(({ theme, height, position }) => ({
  position,
  objectFit: "cover",
  width: "100%",
  height: height,
  display: "none",
  [theme.breakpoints.SML]: {
    display: "block",
  },
}));

interface Props {
  fullHeightContent?: boolean;
  data: TextOverImageData;
}

/**
 * Add text to an image that can be filled as a background, scrim, or hidden.
 */

export const TextOverImage = ({
  fullHeightContent,
  data: {
    desktopImage,
    desktopTextColumnSpan,
    desktopTextColumnPosition,
    fill,
    horizontalAlignment,
    imageSpan,
    mobileImage,
    moduleLayout,
    mobileHeight,
    desktopHeight,
    minHeight,
    tabletTextColumnSpan,
    tabletTextColumnPosition,
    textRows: { textRows, maxWidth },
    verticalAlignment,
    textBottomPadding,
    imageAlignment,
    horizontalPadding: horizontalPaddingInput,
  },
}: Props) => {
  const { theme } = moduleLayout;
  const colors = getModuleThemeColors(theme);
  const { navHeights } = useTheme();

  const height = {
    mobile: `min(calc(${mobileHeight ?? 100}vh - ${
      navHeights.mobile
    }px) , 700px)`,
    desktop: fullHeightContent
      ? "100%"
      : `min(calc(${desktopHeight ?? 100}vh - ${
          navHeights.desktop
        }px), 1200px)`,
  };
  const horizontalPadding = resolveHorizontalPadding(horizontalPaddingInput);

  const gridColumnProps = useMemo((): ColumnProps => {
    const getDesktopStart = (span: ColNumber): ColNumber => {
      switch (horizontalAlignment) {
        case "left":
          return 1;
        case "center":
          return (maxDesktopGridSpan / 2 -
            Math.ceil(span / 2 - 1)) as ColNumber;
        case "right":
          return (maxDesktopGridSpan + 1 - span) as ColNumber;
      }
    };
    const getTabletStart = (span: MEDColNumber): MEDColNumber => {
      switch (horizontalAlignment) {
        case "left":
          return 1;
        case "center":
          return (maxTabletGridSpan / 2 -
            Math.ceil(span / 2 - 1)) as MEDColNumber;
        case "right":
          return (maxTabletGridSpan + 1 - span) as MEDColNumber;
      }
    };

    const getMobileSpan = (
      fill: TextOverImageFill,
      alignment: TextOverImageHorizontalAlignment,
    ): SMLGridColumnEnd => {
      if (fill === "background" && alignment !== "center") {
        return "span 3";
      }
      return "span 4";
    };

    return {
      LRG: {
        end: `span ${desktopTextColumnSpan}`,
        row: 1,
        start:
          desktopTextColumnPosition || getDesktopStart(desktopTextColumnSpan),
      },
      MED: {
        end: `span ${tabletTextColumnSpan}`,
        row: 1,
        start: tabletTextColumnPosition || getTabletStart(tabletTextColumnSpan),
      },
      SML: {
        end: getMobileSpan(fill, horizontalAlignment),
        row: 1,
        start: horizontalAlignment === "right" && fill === "background" ? 2 : 1,
      },
    };
  }, [
    desktopTextColumnSpan,
    horizontalAlignment,
    tabletTextColumnSpan,
    fill,
    desktopTextColumnPosition,
    tabletTextColumnPosition,
  ]);

  const imageAlignmentObjectPosition = useMemo(() => {
    if (typeof imageAlignment !== "string") {
      return undefined;
    }

    switch (imageAlignment) {
      case "top-left":
        return "top left";
      case "top-center":
        return "top center";
      case "top-right":
        return "top right";
      case "center-left":
        return "center left";
      case "center-center":
        return "center center";
      case "center-right":
        return "center right";
      case "bottom-left":
        return "bottom left";
      case "bottom-center":
        return "bottom center";
      case "bottom-right":
        return "bottom right";
      default:
        return undefined;
    }
  }, [imageAlignment]);

  const getPosition = (
    mediaStyle: IBynderImageWithBorderRadius["mediaStyle"],
  ) => {
    return (!mediaStyle?.borderRadius || mediaStyle.borderRadius === "0px") &&
      imageSpan === "full-width"
      ? "absolute"
      : undefined;
  };
  const renderMedia = () => {
    return (
      <>
        {/* Desktop */}
        <ImageBorderRadiusWrapper
          mediaStyle={desktopImage.mediaStyle}
          position={imageSpan === "full-width" ? "absolute" : undefined}
          display={{ LRG: "block", MED: "block", SML: "none" }}
        >
          {fill === "scrim" ? (
            <ScrimMedia
              media={desktopImage}
              contentAlignX={horizontalAlignment}
              contentAlignY={verticalAlignment}
              height={height.desktop}
              imageSpan={imageSpan}
              maxWidth={3000}
              imageAlignment={imageAlignmentObjectPosition}
              isDesktop
            />
          ) : (
            <DesktopMedia
              loopVideo={!desktopImage.disableVideoLoop}
              showVideoControls={desktopImage.showVideoControls}
              maxWidth={3000}
              asset={desktopImage.media}
              thumbnail={desktopImage.thumbnail}
              height={height.desktop}
              position={getPosition(desktopImage.mediaStyle)}
              imageAlignment={imageAlignmentObjectPosition}
            />
          )}
        </ImageBorderRadiusWrapper>
        {/* Mobile */}
        <ImageBorderRadiusWrapper
          mediaStyle={mobileImage.mediaStyle}
          position={imageSpan === "full-width" ? "absolute" : undefined}
          display={{ LRG: "none", MED: "none", SML: "block" }}
        >
          {fill === "scrim" ? (
            <ScrimMedia
              media={mobileImage}
              contentAlignX={horizontalAlignment}
              contentAlignY={verticalAlignment}
              height={height.mobile}
              imageSpan={imageSpan}
              maxWidth={1000}
            />
          ) : (
            <MobileMedia
              loopVideo={!mobileImage.disableVideoLoop}
              showVideoControls={mobileImage.showVideoControls}
              maxWidth={1000}
              asset={mobileImage.media}
              thumbnail={mobileImage.thumbnail}
              height={height.mobile}
              position={getPosition(mobileImage.mediaStyle)}
            />
          )}
        </ImageBorderRadiusWrapper>
      </>
    );
  };

  const bottomPadding = (textBottomPadding as SpacingArrayValues) ?? "none";

  return (
    <ModuleLayout fullHeightContent={fullHeightContent} {...moduleLayout}>
      <CustomBreakpoints>
        <Container
          height={fullHeightContent ? "100%" : "auto"}
          justifyContent="center"
          position={"relative"}
        >
          {imageSpan === "full-width" && renderMedia()}
          <FullHeightGrid
            minHeight={minHeight}
            height={height}
            horizontalPadding={horizontalPadding}
          >
            {imageSpan === "grid-width" && (
              <Column
                LRG={{
                  start: 1,
                  end: "span 24",
                  row: 1,
                }}
                MED={{
                  start: 1,
                  end: "span 12",
                  row: 1,
                }}
                SML={{
                  start: 1,
                  end: "span 4",
                  row: 1,
                }}
              >
                <Container position="relative">{renderMedia()}</Container>
              </Column>
            )}
            <TopColumn {...gridColumnProps}>
              <Container alignItems={verticalAlignment} height="100%">
                <Container
                  backgroundColor={
                    fill === "background"
                      ? colors.standard.background
                      : undefined
                  }
                  width="100%"
                  padding={{
                    LRG: [0, 0, bottomPadding, 0],
                    MED: [0, 0, bottomPadding, 0],
                    SML: [0, 0, bottomPadding, 0],
                  }}
                >
                  <BlockRows
                    maxWidth={maxWidth}
                    rows={textRows}
                    theme={
                      // We use PrimaryInk for scrim
                      // we use the theme set in module for others
                      fill === "scrim" ? "PrimaryInk" : theme
                    }
                    alignment={
                      horizontalAlignment === "center" ? "center" : "left"
                    }
                  />
                </Container>
              </Container>
            </TopColumn>
          </FullHeightGrid>
        </Container>
      </CustomBreakpoints>
    </ModuleLayout>
  );
};
